import { useId, useRef, useState } from 'react';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { SendResultPanel } from '../compose/SendResultPanel';
import { SentActivityPanel } from './SentActivityPanel';
import { SentMessagesTable } from './SentMessagesTable';
import { SentDetailMeta } from './SentDetailMeta';
import { SentMessagePreview } from './SentMessagePreview';
import { useSentActivity } from '../../hooks/useSentActivity';

/**
 * Communications Home "Sent" overview (PRO-0620, gated on
 * `UCP_COMMS_HOME_ACTIVITY_ENABLED` + single-school sender by the host). Owns the
 * `useSentActivity` fetch + the selected-row detail state so the hook only mounts
 * when the section is entitled (no fetch for non-senders / flag-off). Activating a
 * row reuses the shipped counts-only `SendResultPanel` (`GET /sends/:id/result`,
 * AC11) rendered in a right-anchored `Sheet` reading-pane (not an inline panel) so
 * the detail is always in view regardless of how long the table has grown; the
 * `Sheet` handles focus-trap, Esc/backdrop close and focus-return (NF-10).
 *
 * The drawer renders, in order: a meta panel (`SentDetailMeta`, from the already-
 * loaded `CommsSentRow` — no new PII), a PII-safe message preview
 * (`SentMessagePreview`, authored token-form body with placeholder chips), then
 * the counts-only `SendResultPanel` (delivery). All behind the same
 * `UCP_COMMS_HOME_ACTIVITY_ENABLED` gate as the section itself.
 */
export function SentActivitySection() {
  const {
    summary,
    rows,
    status,
    hasMore,
    loadingMore,
    range,
    rangeOptions,
    setRange,
    loadMore,
    retry,
  } = useSentActivity();
  const [selectedSendId, setSelectedSendId] = useState<string | null>(null);
  const detailTitleId = useId();
  // The activated row, so the Sheet can return focus to it on close (NF-10).
  const triggerRef = useRef<HTMLElement | null>(null);

  const openRow = (sendId: string) => {
    triggerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    setSelectedSendId(sendId);
  };
  const closeDetail = () => setSelectedSendId(null);

  const selectedRow =
    selectedSendId !== null ? (rows.find((row) => row.sendId === selectedSendId) ?? null) : null;

  return (
    <section aria-labelledby="sent-activity-heading" className="mt-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 id="sent-activity-heading" className="m-0 text-lg font-semibold text-text-primary">
          Recently sent
        </h2>
        <Select
          label="Time range"
          containerClassName="flex items-center gap-2"
          className="w-auto"
          value={String(range)}
          onChange={(event) => setRange(Number(event.target.value))}
        >
          {rangeOptions.map((days) => (
            <option key={days} value={days}>
              Last {days} days
            </option>
          ))}
        </Select>
      </div>

      <SentActivityPanel summary={summary} status={status} onRetry={retry} />

      <h3 id="sent-table-heading" className="mb-3 mt-6 text-base font-semibold text-text-primary">
        Sent messages
      </h3>
      <SentMessagesTable
        rows={rows}
        status={status}
        selectedSendId={selectedSendId}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onOpenRow={openRow}
        onLoadMore={loadMore}
        onRetry={retry}
      />

      <Sheet
        open={selectedSendId !== null}
        onClose={closeDetail}
        returnFocusRef={triggerRef}
        anchor="right"
        aria-labelledby={detailTitleId}
        className="w-[92%] max-w-lg"
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
            <h3 id={detailTitleId} className="m-0 text-base font-semibold text-text-primary">
              Send detail
            </h3>
            <Button variant="ghost" size="sm" onClick={closeDetail}>
              Close
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {selectedSendId ? (
              <>
                {selectedRow ? <SentDetailMeta row={selectedRow} /> : null}
                <SentMessagePreview sendId={selectedSendId} />
                <SendResultPanel sendId={selectedSendId} />
              </>
            ) : null}
          </div>
        </div>
      </Sheet>
    </section>
  );
}
